import type { ButtonHTMLAttributes, CSSProperties, HTMLAttributes, ReactNode, SyntheticEvent } from 'react';
import { useEffect } from 'react';

/* ---------- Button ---------- */
export type ButtonVariant = 'primary' | 'ghost' | 'danger';
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant | undefined;
  size?: 'md' | 'sm' | undefined;
  loading?: boolean | undefined;
}
export function Button({
  variant = 'ghost',
  size = 'md',
  loading,
  children,
  className = '',
  disabled,
  ...rest
}: ButtonProps) {
  const cls = ['kx-btn', `kx-btn-${variant}`, size === 'sm' ? 'kx-btn-sm' : '', className].filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? '…' : children}
    </button>
  );
}

/* ---------- Badge ---------- */
export type BadgeTone = 'green' | 'amber' | 'red' | 'blue' | 'purple' | 'grey';
export function Badge({ tone, children }: { tone: BadgeTone; children: ReactNode }) {
  return <span className={`kx-badge ${tone}`}>{children}</span>;
}

/* ---------- Chip ---------- */
/**
 * Gợi ý ngắn gắn vào biểu tượng (bug QA FT-AFF-020: `title` gốc của trình duyệt không hiện ổn định — khung app nhúng, máy cảm ứng). Hiện bong bóng khi rê chuột
 * HOẶC focus (Tab / chạm) — chỉ CSS, không script nội tuyến (CSP); `aria-label` đọc được bằng trình đọc màn hình; vẫn giữ `title` làm dự phòng.
 */
export function InfoTip({ text, children }: { text: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noNoninteractiveTabindex: focus để hiện gợi ý bằng bàn phím / chạm
    <span className="kx-infotip" tabIndex={0} role="img" aria-label={text} title={text}>
      {children}
      <span className="kx-infotip-bubble" role="tooltip" aria-hidden="true">
        {text}
      </span>
    </span>
  );
}

export function Chip({
  active,
  onClick,
  children,
}: {
  active?: boolean | undefined;
  onClick?: (() => void) | undefined;
  children: ReactNode;
}) {
  return (
    <button type="button" className={`kx-chip ${active ? 'active' : ''}`} onClick={onClick} aria-pressed={active}>
      {children}
    </button>
  );
}

/* ---------- Page head ---------- */
export function PageHead({
  title,
  subtitle,
  actions,
  kicker,
}: {
  title: string;
  subtitle?: ReactNode | undefined;
  actions?: ReactNode | undefined;
  /** Nhãn nhỏ trên tiêu đề như mockup v152 (vd "KOVIX.SUPPLY"). */
  kicker?: ReactNode | undefined;
}) {
  // UI test 01/10/2026: tiêu đề trang là <h1> (WCAG 1.3.1/2.4.6 — trước là div, mọi trang không có h1) và đặt tiêu đề tab trình duyệt theo trang
  // (WCAG 2.4.2 — trước mọi trang cùng "KOVIX · F&B SaaS Console", lịch sử/tab trình duyệt không phân biệt được).
  useEffect(() => {
    if (typeof document === 'undefined' || !title) return;
    const prev = document.title;
    document.title = `${title} · KOVIX`;
    return () => {
      document.title = prev;
    };
  }, [title]);
  return (
    <div className="kx-page-head">
      <div>
        {kicker ? <div className="pk">{kicker}</div> : null}
        <h1 className="pt">{title}</h1>
        {subtitle ? <div className="ps">{subtitle}</div> : null}
      </div>
      {actions ? <div className="pa">{actions}</div> : null}
    </div>
  );
}

/* ---------- KPI ---------- */
export type KpiTone = 'a' | 'b' | 'c' | 'd';
export function KpiRow({ children }: { children: ReactNode }) {
  return <div className="kx-kpi-row">{children}</div>;
}
export function Kpi({
  tone,
  label,
  value,
  delta,
  icon,
}: {
  tone: KpiTone;
  label: string;
  value: ReactNode;
  delta?: { dir: 'up' | 'down' | 'flat'; text: string } | undefined;
  icon?: ReactNode | undefined;
}) {
  return (
    <div className={`kx-kpi ${tone}`}>
      <div className="kl">{label}</div>
      <div className="kv">{value}</div>
      {delta ? (
        <div className={`kd ${delta.dir === 'flat' ? '' : delta.dir}`}>
          {delta.dir === 'up' ? '▲' : delta.dir === 'down' ? '▼' : ''} {delta.text}
        </div>
      ) : null}
      {icon ? <div className="kico">{icon}</div> : null}
    </div>
  );
}

/* ---------- Panel ---------- */
export function Panel({
  title,
  subtitle,
  actions,
  noPadding,
  children,
}: {
  title?: string | undefined;
  subtitle?: string | undefined;
  actions?: ReactNode | undefined;
  noPadding?: boolean | undefined;
  children: ReactNode;
}) {
  return (
    <section className="kx-panel">
      {/* FT nhóm E (02/10/2026): Panel không tiêu đề nhưng có nút/mô tả (vd. "+ Tài xế thuê ngoài", "+ Hồ sơ" đơn gia công) trước đây mất cả hàng nút — vẫn dựng đầu panel. */}
      {title || subtitle || actions ? (
        <header className="kx-panel-head">
          <div>
            {title ? <div className="ph">{title}</div> : null}
            {subtitle ? <div className="phs">{subtitle}</div> : null}
          </div>
          {actions ? <div className="pha">{actions}</div> : null}
        </header>
      ) : null}
      <div className={`kx-panel-body ${noPadding ? 'p0' : ''}`}>{children}</div>
    </section>
  );
}

/* ---------- Notice ---------- */
export function Notice({
  tone = 'info',
  icon,
  children,
}: {
  tone?: 'info' | 'warn' | 'bad' | 'good' | undefined;
  icon?: ReactNode | undefined;
  children: ReactNode;
}) {
  return (
    <div className={`kx-notice ${tone}`} role={tone === 'bad' ? 'alert' : 'status'}>
      {icon ? <span>{icon}</span> : null}
      <div>{children}</div>
    </div>
  );
}

/* ---------- Empty ---------- */
export function Empty({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string | undefined;
  action?: ReactNode | undefined;
}) {
  return (
    <div className="kx-empty">
      <b>{title}</b>
      {hint ? <div>{hint}</div> : null}
      {action ? <div style={{ marginTop: 12 }}>{action}</div> : null}
    </div>
  );
}

/* ---------- Skeleton ---------- */
export function Skeleton({ rows = 5 }: { rows?: number | undefined }) {
  return (
    <div style={{ display: 'grid', gap: 10, padding: 18 }} role="status" aria-busy="true" aria-label="Đang tải">
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: dòng giữ chỗ theo vị trí, không có dữ liệu riêng
        <div key={i} className="kx-skeleton" style={{ width: `${70 + ((i * 13) % 30)}%` }} />
      ))}
    </div>
  );
}

/* ---------- ScrollRegion ---------- */
/**
 * Vùng cuộn ngang bọc bảng (thường `className="kx-tblwrap"`): `<section aria-label>` = landmark region có tên,
 * nhận focus để cuộn được bằng bàn phím (axe scrollable-region-focusable, WCAG 2.1.1 — kiểm thử UI 01/10/2026).
 */
export function ScrollRegion({
  label,
  children,
  ...rest
}: { label: string; children: ReactNode } & Omit<HTMLAttributes<HTMLElement>, 'aria-label' | 'role' | 'tabIndex'>) {
  return (
    <section
      // biome-ignore lint/a11y/noNoninteractiveTabindex: vùng cuộn phải nhận focus bàn phím để cuộn ngang (axe scrollable-region-focusable)
      tabIndex={0}
      aria-label={label}
      {...rest}
    >
      {children}
    </section>
  );
}

/* ---------- StopPropagation ---------- */
/**
 * Ranh giới sự kiện quanh cụm nút thao tác nằm trong dòng/thẻ bấm được: chặn click (và phím khi `keys`) nổi lên
 * phần tử cha — bấm nút không kích hoạt luôn hành động của dòng. Bản thân nó không phải điều khiển; các nút bên trong
 * vẫn dùng được bằng bàn phím (Enter/Space trên nút sinh sự kiện click, bị chặn tại đây như chuột).
 */
export function StopPropagation({
  inline,
  keys,
  className,
  style,
  children,
}: {
  /** `span` thay vì `div` (nằm trong dòng chữ / ô inline). */
  inline?: boolean | undefined;
  /** Chặn cả keydown (khi phần tử cha có xử lý phím riêng). */
  keys?: boolean | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
  children: ReactNode;
}) {
  const stop = (e: SyntheticEvent) => e.stopPropagation();
  const onKeyDown = keys ? stop : undefined;
  return inline ? (
    // biome-ignore lint/a11y/noStaticElementInteractions: chỉ chặn sự kiện nổi bọt, không phải điều khiển (xem chú thích hàm)
    <span className={className} style={style} onClick={stop} onKeyDown={onKeyDown}>
      {children}
    </span>
  ) : (
    // biome-ignore lint/a11y/noStaticElementInteractions: chỉ chặn sự kiện nổi bọt, không phải điều khiển (xem chú thích hàm)
    <div className={className} style={style} onClick={stop} onKeyDown={onKeyDown}>
      {children}
    </div>
  );
}

/* ---------- Tabs ---------- */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
}: {
  items: { key: T; label: string }[];
  value: T;
  onChange: (k: T) => void;
}) {
  return (
    <div className="kx-tabs" role="tablist">
      {items.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={value === t.key}
          className={value === t.key ? 'active' : ''}
          onClick={() => onChange(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
